import { mondayOnOrBefore } from "./dayMath";

/** Width of one day on the timeline, in px, for each view. */
export const DAY_WIDTHS = { day: 200, week: 90, month: 27 };

/**
 * Days the horizontal window moves in: the window an interface component reads only changes when the scroll
 * position crosses one of these boundaries, so scrolling inside one step re-renders nothing.
 */
export const WINDOW_QUANTUM_DAYS = { day: 7, week: 7, month: 14 };

/** Allocations are fetched in blocks (tiles) of eight weeks. Tiles start on Mondays because the timeline does. */
export const TILE_DAYS = 56;

/** The timeline extends this many years before and after today. */
export const CANVAS_YEARS = 20;

/**
 * The fixed span of days the timeline can show. It starts on a Monday, a whole number of tiles long, and is
 * never rebased while the user scrolls, so a day always sits at the same pixel for a given view.
 */
export function createTimeline(today, years = CANVAS_YEARS) {
  const halfSpan = Math.round(365.25 * years);
  const originDay = mondayOnOrBefore(today - halfSpan);
  const tileCount = Math.ceil((today + halfSpan - originDay + 1) / TILE_DAYS);
  const totalDays = tileCount * TILE_DAYS;

  return { originDay, tileCount, totalDays, lastDay: originDay + totalDays - 1 };
}

export const canvasWidth = (timeline, dayWidth) => timeline.totalDays * dayWidth;

export const dayToX = (timeline, day, dayWidth) => (day - timeline.originDay) * dayWidth;

/** The day under a pixel offset of the canvas. */
export const xToDay = (timeline, x, dayWidth) => timeline.originDay + Math.floor(x / dayWidth);

export const clampDay = (timeline, day) => Math.min(timeline.lastDay, Math.max(timeline.originDay, day));

export const isInsideTimeline = (timeline, day) => day >= timeline.originDay && day <= timeline.lastDay;

export const tileOf = (timeline, day) => Math.floor((day - timeline.originDay) / TILE_DAYS);

export const tileFirstDay = (timeline, tile) => timeline.originDay + tile * TILE_DAYS;

export const tileLastDay = (timeline, tile) => tileFirstDay(timeline, tile) + TILE_DAYS - 1;

/** The days a horizontal scroll position and viewport width show (closed range, within the timeline). */
export function visibleDays(timeline, scrollLeft, viewportWidth, dayWidth) {
  return {
    firstDay: clampDay(timeline, xToDay(timeline, scrollLeft, dayWidth)),
    lastDay: clampDay(timeline, xToDay(timeline, scrollLeft + viewportWidth, dayWidth)),
  };
}

/**
 * Widens a closed range of days to whole steps of `quantum` days counted from the start of the timeline, so
 * two ranges that differ by less than a step become the same range.
 */
export function quantizeRange(timeline, range, quantum) {
  const first = Math.floor((range.firstDay - timeline.originDay) / quantum) * quantum;
  const last = Math.ceil((range.lastDay - timeline.originDay + 1) / quantum) * quantum - 1;

  return {
    firstDay: clampDay(timeline, timeline.originDay + first),
    lastDay: clampDay(timeline, timeline.originDay + last),
  };
}

/** Horizontal scroll position that puts a day in the middle of a viewport. */
export function scrollLeftForCenter(timeline, day, dayWidth, viewportWidth) {
  return Math.max(0, dayToX(timeline, day, dayWidth) - viewportWidth / 2 + dayWidth / 2);
}

/** The day in the middle of a viewport scrolled to `scrollLeft`. */
export function centerDay(timeline, scrollLeft, viewportWidth, dayWidth) {
  return clampDay(timeline, xToDay(timeline, scrollLeft + viewportWidth / 2, dayWidth));
}
